import { z } from "zod";
import { LichessSchema, LichessGameSource } from "./lichess";
import {
  CoachMode,
  Color,
  Workspace,
  LiveReview,
  type AppState,
} from "./types";
const id = z.string().max(120);
const time = z.number().finite().nonnegative();
const moves = z
  .array(z.string().regex(/^[a-h][1-8][a-h][1-8][qrbn]?$/))
  .max(1000);
const fen = z.string().max(200);
const title = z.string().max(500);
const text = z.string().max(15000);
const line = z.object({
  moves,
  san: z.array(z.string().max(20)).max(1000),
  cp: z.number().finite().nullable(),
  mate: z.number().finite().nullable(),
  depth: z.number().int().min(0).max(300),
});
const game = z.object({
  source: LichessGameSource.optional(),
  id,
  title,
  createdAt: time,
  updatedAt: time,
  initialFen: fen,
  moves,
  revision: z.number().int().min(0),
  mode: z.enum(["analysis", "play", "import"]),
  humanColor: Color,
  orientation: Color,
  difficulty: z.enum(["gentle", "balanced", "challenging"]),
  coaching: CoachMode,
  hintFirst: z.boolean(),
  liveReview: LiveReview.optional(),
  liveReviewFrom: z.number().int().nonnegative().optional(),
  result: z.enum(["*", "1-0", "0-1", "1/2-1/2"]),
  endReason: title.optional(),
  pinned: z.boolean(),
  headers: z.record(z.string().max(40), z.string().max(500)),
  clock: z.object({
    initial: time,
    increment: time,
    w: time,
    b: time,
    startedAt: time.nullable(),
  }),
});
const exercise = z.object({
  id,
  title,
  fen,
  solution: moves,
  san: z.array(z.string().max(20)),
  evidenceId: id.optional(),
  gameId: id.optional(),
  source: z.enum(["personal", "curriculum"]),
  concept: text,
  depth: z.number().int().nonnegative(),
  attempts: z
    .array(
      z.object({
        at: time,
        outcome: z.enum(["independent", "hinted", "revealed", "incorrect"]),
        move: z.string().max(10),
      }),
    )
    .max(10000),
  dueAt: time,
  variant: z.enum(["original", "perspective"]).optional(),
});
export const StateSchema = z.object({
  lichess: LichessSchema.optional(),
  version: z.literal(1),
  revision: z.number().int().nonnegative(),
  workspace: Workspace.nullable(),
  games: z.array(game).max(1000),
  activeGameId: id.nullable(),
  variations: z
    .array(
      z.object({
        id,
        gameId: id,
        sourcePly: z.number().int().nonnegative(),
        liveReviewFrom: z.number().int().nonnegative().optional(),
        title,
        initialFen: fen,
        moves,
        revision: z.number().int().nonnegative(),
        pinned: z.boolean(),
      }),
    )
    .max(2000),
  activeVariationId: id.nullable(),
  viewPly: z.number().int().nonnegative().nullable(),
  messages: z
    .array(
      z.object({
        id,
        role: z.enum(["user", "assistant", "system"]),
        text,
        at: time,
        reference: z
          .object({
            gameId: id,
            ply: z.number().int().nonnegative(),
            variationId: id.optional(),
          })
          .optional(),
      }),
    )
    .max(300),
  evidence: z
    .array(
      z.object({
        id,
        gameId: id,
        ply: z.number().int().min(1),
        fen,
        played: z.string().max(10),
        best: z.string().max(10),
        loss: z.number().finite().nonnegative(),
        pattern: z.enum([
          "missed-mate",
          "allowed-mate",
          "missed-capture",
          "lost-advantage",
        ]),
        before: line,
        after: line,
        at: time,
        dismissed: z.boolean(),
        note: z.string().max(500),
      }),
    )
    .max(10000),
  exercises: z.array(exercise).max(10000),
  practice: z
    .object({
      exerciseIds: z.array(id),
      index: z.number().int().nonnegative(),
      hintLevel: z.number().int().min(0).max(3),
      solved: z.boolean(),
      feedback: text,
      attempts: z.number().int().nonnegative(),
    })
    .nullable(),
  plans: z.array(
    z.object({
      id,
      title,
      minutes: z.number().int().nonnegative(),
      items: z.array(
        z.object({
          title,
          minutes: z.number().int().min(1).max(120),
          exerciseIds: z.array(id),
          done: z.boolean(),
        }),
      ),
    }),
  ),
  activePlanId: id.nullable(),
  lesson: z.enum(["opposition", "development", "calculation"]).nullable(),
  preferences: z.object({
    coaching: CoachMode,
    hintFirst: z.boolean(),
    length: z.enum(["brief", "standard", "deep"]),
    side: Color,
    speech: z.boolean(),
    handsFree: z.boolean(),
    arrows: z.boolean(),
    memory: z.boolean(),
    liveReview: LiveReview.default("mistakes"),
  }),
  libraryFilter: z.object({
    query: title,
    result: title,
    color: title,
    after: title,
    before: title,
    opening: title,
    opponent: title,
    timeControl: title,
  }),
  analysis: z
    .object({
      fen,
      gameId: id,
      revision: z.number().int().nonnegative(),
      variationId: id.nullable(),
      lines: z.array(line),
      engine: title,
      at: time,
    })
    .nullable(),
  comparison: z.array(z.object({ move: title, line })),
  moveReviews: z
    .array(
      z.object({
        id,
        gameId: id,
        variationId: id.nullable(),
        ply: z.number().int().min(1),
        historyKey: z.string().max(10000),
        beforeFen: fen,
        afterFen: fen,
        played: z.string().max(10),
        san: z.string().max(20),
        color: Color,
        loss: z.number().finite().nonnegative(),
        before: line,
        after: line,
        text,
        explanation: z.enum(["coach", "engine"]),
        at: time,
      }),
    )
    .max(2000)
    .default([]),
  annotations: z
    .object({
      fen,
      arrows: z
        .array(
          z.object({
            from: z.string().regex(/^[a-h][1-8]$/),
            to: z.string().regex(/^[a-h][1-8]$/),
            label: z.string().max(80).optional(),
          }),
        )
        .max(8),
      squares: z.array(z.string().regex(/^[a-h][1-8]$/)).max(16),
    })
    .nullable()
    .optional(),
  teaching: z
    .object({ gameId: id, title, text, question: z.string().max(500) })
    .nullable()
    .optional(),
  studio: z
    .object({
      kind: z.enum([
        "opening",
        "calculation",
        "teachback",
        "conversion",
        "defense",
        "preparation",
      ]),
      title,
      description: text,
      question: text,
      gameId: id,
      attempts: z.array(
        z.object({ line: z.array(z.string()), feedback: text, at: time }),
      ),
      referenceGameIds: z.array(id),
    })
    .nullable()
    .optional(),
});
export function validateState(value: unknown): AppState {
  const parsed = StateSchema.safeParse(value);
  if (!parsed.success)
    throw new Error(
      "The saved workspace format is invalid. Export your data before attempting recovery; it has not been overwritten.",
    );
  return parsed.data as AppState;
}
